export type PostAuthNavigationState = {
  requiresBasicInfoCollection: boolean;
  requiresOrganizationCreation: boolean;
  requiresOrganizationSelection: boolean;
};

export type PostAuthNavigationRouteKind =
  'application' | 'basic-info' | 'create-organization' | 'select-organization';

export type PostAuthSetupRouteName =
  'collect-basic-info' | 'create-organization' | 'select-organization';

export type PostAuthSetupRouteLocation = {
  name: PostAuthSetupRouteName;
  query: { redirect: string };
};

export type PostAuthNavigationDecision =
  | { kind: 'allow' }
  | { kind: 'route'; route: PostAuthSetupRouteLocation }
  | { kind: 'redirect-target'; target: string };

type ResolvePostAuthNavigationOptions = {
  currentRoute: PostAuthNavigationRouteKind;
  redirectTarget: string;
  state: PostAuthNavigationState;
};

type PostAuthSetupRule = {
  allowedRoutes: readonly PostAuthNavigationRouteKind[];
  required: keyof PostAuthNavigationState;
  routeName: PostAuthSetupRouteName;
};

// Post-auth setup routing is driven by canonical session/user-context state so reloads and
// deep links still land on the required onboarding step; no transient continuation flag gates it.
const POST_AUTH_SETUP_RULES = [
  {
    allowedRoutes: ['basic-info'],
    required: 'requiresBasicInfoCollection',
    routeName: 'collect-basic-info',
  },
  {
    allowedRoutes: ['create-organization'],
    required: 'requiresOrganizationCreation',
    routeName: 'create-organization',
  },
  {
    allowedRoutes: ['create-organization', 'select-organization'],
    required: 'requiresOrganizationSelection',
    routeName: 'select-organization',
  },
] satisfies readonly PostAuthSetupRule[];

const createSetupRoute = (
  name: PostAuthSetupRouteName,
  redirectTarget: string,
): PostAuthSetupRouteLocation => ({
  name,
  query: { redirect: redirectTarget },
});

export const resolvePostAuthNavigation = ({
  currentRoute,
  redirectTarget,
  state,
}: ResolvePostAuthNavigationOptions): PostAuthNavigationDecision => {
  const requiredSetup = POST_AUTH_SETUP_RULES.find((rule) => state[rule.required]);

  if (requiredSetup) {
    if (requiredSetup.allowedRoutes.some((route) => route === currentRoute)) {
      return { kind: 'allow' };
    }

    return { kind: 'route', route: createSetupRoute(requiredSetup.routeName, redirectTarget) };
  }

  if (currentRoute === 'basic-info') {
    return { kind: 'redirect-target', target: redirectTarget };
  }

  return { kind: 'allow' };
};
